import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Gift, Loader2, Ticket, UserRound } from "lucide-react";
import { toast } from "sonner";

import { guestRewardsApi } from "@/api/guest/guestRewardsApi.js";
import GuestLayout from "@/components/guest/GuestLayout.jsx";
import { Button } from "@/components/ui/button";
import { useSession } from "@/hooks/useSession.js";
import { formatGuestDate, formatMoney, getApiData, getApiMessage, read } from "@/utils/guestRewardsFormat.js";

const GuestProfilePage = () => {
  const navigate = useNavigate();
  const { session, hotel, room, loading: sessionLoading } = useSession();
  const [profileState, setProfileState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionLoading && !session) navigate("/guest/expired", { replace: true });
  }, [sessionLoading, session, navigate]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!session) return;
      try {
        setLoading(true);
        const response = await guestRewardsApi.getProfile();
        setProfileState(getApiData(response));
      } catch (error) {
        toast.error(getApiMessage(error, "Unable to load your profile."));
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [session]);

  if (sessionLoading) return <FullScreenLoading />;
  if (!session) return null;

  const guest = read(profileState, "guest", "Guest");
  const requiresRegistration = read(profileState, "requiresRegistration", "RequiresRegistration", true);

  return (
    <>
      <Helmet>
        <title>My profile - Guest Portal</title>
      </Helmet>
      <GuestLayout hotel={hotel} room={room} session={session}>
        <GuestPageShell onBack={() => navigate("/guest/home")}>
          <PageHeader icon={UserRound} title="My profile" subtitle="View your guest profile and stay summary." />
          {loading ? (
            <LoadingBlock text="Loading profile..." />
          ) : requiresRegistration || !guest ? (
            <RegistrationPrompt onRegister={() => navigate("/guest/register")} />
          ) : (
            <div className="space-y-4">
              <div className="rounded-2xl border bg-white p-4">
                <p className="text-xl font-bold text-slate-900">{read(guest, "fullName", "FullName", "Guest")}</p>
                <p className="mt-1 text-sm text-slate-600">
                  {[read(guest, "phoneNumber", "PhoneNumber"), read(guest, "email", "Email")].filter(Boolean).join(" · ") ||
                    "Contact details not set"}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <SummaryCard label="Total stays" value={read(guest, "totalStays", "TotalStays", 0)} />
                <SummaryCard label="Lifetime spend" value={formatMoney(read(guest, "totalSpend", "TotalSpend", 0))} />
                <SummaryCard label="Available points" value={read(guest, "availableLoyaltyPoints", "AvailableLoyaltyPoints", 0)} icon={Gift} />
                <SummaryCard label="Last stay" value={formatGuestDate(read(guest, "lastStayAt", "LastStayAt"))} icon={Ticket} />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Button type="button" variant="outline" onClick={() => navigate("/guest/loyalty")}>
                  View loyalty points
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate("/guest/coupons")}>
                  View coupons
                </Button>
              </div>
            </div>
          )}
        </GuestPageShell>
      </GuestLayout>
    </>
  );
};

export const FullScreenLoading = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-12 w-12 animate-spin text-primary" />
  </div>
);

export const GuestPageShell = ({ children, onBack }) => (
  <div className="mx-auto max-w-4xl space-y-5">
    <Button type="button" variant="ghost" onClick={onBack} className="bg-white/85 text-slate-800 hover:bg-white">
      <ArrowLeft className="mr-2 h-4 w-4" />
      Back
    </Button>
    <section className="rounded-3xl border border-white/75 bg-white/90 p-5 shadow-[0_22px_52px_rgba(15,23,42,0.18)] backdrop-blur-xl sm:p-6">
      {children}
    </section>
  </div>
);

export const PageHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-5 flex items-start gap-3">
    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#923536]/10 text-[#923536]">
      <Icon className="h-6 w-6" />
    </div>
    <div>
      <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
      <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
    </div>
  </div>
);

export const LoadingBlock = ({ text }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-slate-600">
    <Loader2 className="h-10 w-10 animate-spin text-[#923536]" />
    <span>{text}</span>
  </div>
);

export const RegistrationPrompt = ({ onRegister }) => (
  <div className="rounded-2xl border border-dashed p-6 text-center">
    <p className="text-base font-semibold text-slate-900">Register to unlock your rewards.</p>
    <p className="mt-2 text-sm text-slate-600">
      Create your guest profile to view loyalty points, memberships, and coupons for this stay.
    </p>
    <Button type="button" onClick={onRegister} className="mt-4 bg-[#923536] text-white hover:bg-[#7f2d2e]">
      Register now
    </Button>
  </div>
);

export const SummaryCard = ({ label, value, icon: Icon }) => (
  <div className="rounded-2xl border bg-white p-4">
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm text-slate-500">{label}</p>
      {Icon ? <Icon className="h-4 w-4 text-[#923536]" /> : null}
    </div>
    <p className="mt-2 text-lg font-bold text-slate-900">{value ?? "—"}</p>
  </div>
);

export default GuestProfilePage;
